import { Chain, ClientConfig, createPublicClient, http, zeroAddress } from "viem";

import {
  ARBITRUM,
  AVALANCHE,
  AVALANCHE_FUJI,
  BOTANIX,
  ASSETCHAIN,
  ASSETCHAIN_TESTNET,
  UiContractsChain,
  getViemChain,
} from "config/chains";
import { isWebWorker } from "config/env";
import {
  MulticallErrorEvent,
  MulticallFallbackRpcModeCounter,
  MulticallRequestCounter,
  MulticallRequestTiming,
  MulticallTimeoutEvent,
} from "lib/metrics";
import { emitMetricCounter, emitMetricEvent, emitMetricTiming } from "lib/metrics/emitMetricEvent";
import { getProviderNameFromUrl } from "lib/rpc/getProviderNameFromUrl";
import { sleep } from "lib/sleep";
import { SlidingWindowFallbackSwitcher } from "lib/slidingWindowFallbackSwitcher";
import { abis as allAbis } from "sdk/abis";

import type { MulticallRequestConfig, MulticallResult } from "./types";
import { serializeMulticallErrors } from "./utils";

export const MAX_TIMEOUT = 20000;

export type MulticallProviderUrls = {
  primary: string;
  secondary: string;
};

const DEFAULT_BATCH_CONFIG = {
  http: { batchSize: 0, wait: 0 },
  client: { multicall: { batchSize: 1024 * 1024, wait: 0 } },
};

const BATCH_CONFIGS: Record<
  UiContractsChain,
  {
    http: {
      batchSize: number;
      wait: number;
    };
    client: ClientConfig["batch"];
  }
> = {
  [ARBITRUM]: {
    http: {
      batchSize: 0, // disable batches, here batchSize is the number of eth_calls in a batch
      wait: 0, // keep this setting in case batches are enabled in future
    },
    client: {
      multicall: {
        batchSize: 1024 * 1024, // here batchSize is the number of bytes in a multicall
        wait: 0, // zero delay means formation of a batch in the current macro-task, like setTimeout(fn, 0)
      },
    },
  },
  [AVALANCHE]: {
    http: {
      batchSize: 0,
      wait: 0,
    },
    client: {
      multicall: {
        batchSize: 1024 * 1024,
        wait: 0,
      },
    },
  },
  [AVALANCHE_FUJI]: {
    http: {
      batchSize: 40,
      wait: 0,
    },
    client: {
      multicall: {
        batchSize: 1024 * 1024,
        wait: 0,
      },
    },
  },
  [BOTANIX]: {
    http: {
      batchSize: 0,
      wait: 0,
    },
    client: {
      multicall: {
        batchSize: 1024 * 1024,
        wait: 0,
      },
    },
  },
  [ASSETCHAIN]: {
    http: { batchSize: 0, wait: 0 },
    client: { multicall: { batchSize: 1024 * 1024, wait: 0 } },
  },
  [ASSETCHAIN_TESTNET]: {
    http: { batchSize: 0, wait: 0 },
    client: { multicall: { batchSize: 1024 * 1024, wait: 0 } },
  },
};

export class Multicall {
  static instances: {
    [chainId: number]: Multicall | undefined;
  } = {};

  static async getInstance(chainId: number, abFlags: Record<string, boolean>) {
    let instance = Multicall.instances[chainId];

    if (!instance || instance.chainId !== chainId) {
      instance = new Multicall(chainId, abFlags);

      Multicall.instances[chainId] = instance;
    }

    return instance;
  }

  static getViemClient(chainId: number, rpcUrl: string) {
    const cfg = (BATCH_CONFIGS as Record<number, typeof DEFAULT_BATCH_CONFIG>)[chainId] ?? DEFAULT_BATCH_CONFIG;
    return createPublicClient({
      transport: http(rpcUrl, {
        // retries works strangely in viem, so we disable them
        retryCount: 0,
        retryDelay: 10000000,
        batch: cfg.http,
        timeout: MAX_TIMEOUT,
      }),
      pollingInterval: undefined,
      batch: cfg.client,
      chain: getViemChain(chainId) as Chain,
    });
  }

  fallbackRpcSwitcher = new SlidingWindowFallbackSwitcher({
    fallbackTimeout: 60 * 1000, // 1 minute
    restoreTimeout: 5 * 60 * 1000, // 5 minutes
    eventsThreshold: 3,
    onFallback: () => {
      emitMetricCounter<MulticallFallbackRpcModeCounter>({
        event: "multicall.fallbackRpcMode.on",
        data: {
          isInMainThread: !isWebWorker,
        },
      });
    },
    onRestore: () => {
      emitMetricCounter<MulticallFallbackRpcModeCounter>({
        event: "multicall.fallbackRpcMode.off",
        data: {
          isInMainThread: !isWebWorker,
        },
      });
    },
  });

  getClient: (options?: { forceFallback?: boolean }) => ReturnType<typeof Multicall.getViemClient>;

  constructor(
    public chainId: number,
    private abFlags: Record<string, boolean>
  ) {}

  async call(
    providerUrls: MulticallProviderUrls,
    request: MulticallRequestConfig<any>,
    maxTimeout: number,
    isLargeAccount: boolean
  ) {
    const originalKeys: {
      contractKey: string;
      callKey: string;
    }[] = [];

    const abis: any = {};

    const encodedPayload: { address: string; abi: any; functionName: string; args: any }[] = [];
    const skippedMask: boolean[] = [];

    const contractKeys = Object.keys(request);

    contractKeys.forEach((contractKey) => {
      const contractCallConfig = request[contractKey];

      if (!contractCallConfig) {
        return;
      }

      Object.keys(contractCallConfig.calls).forEach((callKey) => {
        const call = contractCallConfig.calls[callKey];

        if (!call) {
          return;
        }

        // Add Errors ABI to each contract ABI to correctly parse errors
        abis[contractCallConfig.contractAddress] = abis[contractCallConfig.contractAddress] || [
          ...allAbis[contractCallConfig.abiId],
          ...allAbis.CustomErrors,
        ];

        const abi = abis[contractCallConfig.contractAddress];

        // Skip calls targeting zeroAddress to avoid aggregate3 "0x" errors when contracts are not deployed yet.
        originalKeys.push({ contractKey, callKey });
        if (contractCallConfig.contractAddress.toLowerCase() === zeroAddress) {
          skippedMask.push(true);
        } else {
          skippedMask.push(false);
          encodedPayload.push({
            address: contractCallConfig.contractAddress,
            functionName: call.methodName,
            args: call.params,
            abi,
          });
        }
      });
    });

    const providerUrl = this.fallbackRpcSwitcher?.isFallbackMode ? providerUrls.secondary : providerUrls.primary;
    const client = Multicall.getViemClient(this.chainId, providerUrl);
    const rpcProviderName = getProviderNameFromUrl(providerUrl);

    const sendCounterEvent = (
      event: "call" | "timeout" | "error",
      { requestType, rpcProvider }: { requestType: "initial" | "retry"; rpcProvider: string }
    ) => {
      emitMetricCounter<MulticallRequestCounter>({
        event: `multicall.request.${event}`,
        data: {
          isInMainThread: !isWebWorker,
          requestType,
          rpcProvider,
          isLargeAccount,
        },
      });
    };

    const sendTiming = ({
      time,
      requestType,
      rpcProvider,
    }: {
      time: number;
      requestType: "initial" | "retry";
      rpcProvider: string;
    }) => {
      emitMetricTiming<MulticallRequestTiming>({
        event: "multicall.request.timing",
        time,
        data: {
          requestType,
          rpcProvider,
          isLargeAccount,
        },
      });
    };

    const processResponse = (response: any) => {
      const multicallResult: MulticallResult<any> = {
        success: true,
        errors: {},
        data: {},
      };

      response.forEach(({ result, status, error }, i) => {
        const { contractKey, callKey } = originalKeys[i];

        if (status === "success") {
          let values: any;

          if (Array.isArray(result) || typeof result === "object") {
            values = result;
          } else {
            values = [result];
          }

          multicallResult.data[contractKey] = multicallResult.data[contractKey] || {};
          multicallResult.data[contractKey][callKey] = {
            contractKey,
            callKey,
            returnValues: values,
            success: true,
          };
        } else {
          multicallResult.success = false;

          multicallResult.errors[contractKey] = multicallResult.errors[contractKey] || {};
          multicallResult.errors[contractKey][callKey] = error;

          multicallResult.data[contractKey] = multicallResult.data[contractKey] || {};
          multicallResult.data[contractKey][callKey] = {
            contractKey,
            callKey,
            returnValues: [],
            success: false,
            error: error,
          };
        }
      });

      return multicallResult;
    };

    const runSingleCalls = async (clientToUse: ReturnType<typeof Multicall.getViemClient>) => {
      const settled = await Promise.all(
        (encodedPayload as any[]).map(async (c) => {
          try {
            const res = await clientToUse.readContract({
              address: c.address as any,
              abi: c.abi,
              functionName: c.functionName,
              args: c.args,
            } as any);
            return { status: "success", result: res };
          } catch (err: any) {
            return { status: "failure", error: err } as any;
          }
        })
      );

      return settled;
    };

    const fallbackMulticall = (e: Error) => {
      const fallbackProviderUrl = providerUrls.secondary;
      const fallbackProviderName = getProviderNameFromUrl(fallbackProviderUrl);

      sendCounterEvent("call", {
        requestType: "retry",
        rpcProvider: fallbackProviderName,
      });

      // eslint-disable-next-line no-console
      console.groupCollapsed("multicall error:");
      // eslint-disable-next-line no-console
      console.error(e);
      // eslint-disable-next-line no-console
      console.groupEnd();

      if (!this.fallbackRpcSwitcher?.isFallbackMode) {
        this.fallbackRpcSwitcher?.trigger();
      }

      // eslint-disable-next-line no-console
      console.debug(`using multicall fallback for chain ${this.chainId}`);

      const fallbackClient = Multicall.getViemClient(this.chainId, fallbackProviderUrl);

      const durationStart = performance.now();

      const tryAggregate = () =>
        fallbackClient.multicall({ contracts: encodedPayload as any }).catch((_viemError) => {
          // If multicall3 missing or returns no data, fallback to single calls
          const msg = String(_viemError?.message || "");
          const noAggregate =
            msg.includes("aggregate3") || msg.includes("returned no data") || msg.includes("multicall");
          if (noAggregate) {
            return runSingleCalls(fallbackClient);
          }
          throw _viemError;
        });

      return tryAggregate()
        .then((response) => {
          sendTiming({
            time: Math.round(performance.now() - durationStart),
            requestType: "retry",
            rpcProvider: fallbackProviderName,
          });

          return response;
        })
        .catch((_viemError) => {
          const e = new Error(_viemError.message.slice(0, 150));
          // eslint-disable-next-line no-console
          console.groupCollapsed("multicall fallback error:");
          // eslint-disable-next-line no-console
          console.error(e);
          // eslint-disable-next-line no-console
          console.groupEnd();

          emitMetricEvent<MulticallErrorEvent>({
            event: "multicall.error",
            isError: true,
            data: {
              requestType: "retry",
              rpcProvider: fallbackProviderName,
              isInMainThread: !isWebWorker,
              errorMessage: _viemError.message,
            },
          });

          sendCounterEvent("error", {
            requestType: "retry",
            rpcProvider: fallbackProviderName,
          });

          throw e;
        });
    };

    sendCounterEvent("call", {
      requestType: "initial",
      rpcProvider: rpcProviderName,
    });

    const durationStart = performance.now();

    const tryInitialAggregate = () =>
      client.multicall({ contracts: encodedPayload as any }).catch((_viemError) => {
        const msg = String(_viemError?.message || "");
        const noAggregate = msg.includes("aggregate3") || msg.includes("returned no data") || msg.includes("multicall");
        if (noAggregate) {
          return runSingleCalls(client);
        }
        throw _viemError;
      });

    const result = await Promise.race([
      tryInitialAggregate(),
      sleep(maxTimeout).then(() => Promise.reject(new Error("multicall timeout"))),
    ])
      .then((response) => {
        sendTiming({
          time: Math.round(performance.now() - durationStart),
          requestType: "initial",
          rpcProvider: rpcProviderName,
        });

        // Rebuild response to include placeholders for skipped zeroAddress calls in the original order
        let cursor = 0;
        const ordered = originalKeys.map((_, idx) =>
          skippedMask[idx]
            ? { status: "failure", error: new Error("zeroAddress target"), result: undefined }
            : response[cursor++]
        );

        return processResponse(ordered);
      })
      .catch((_viemError) => {
        const e = new Error(_viemError.message.slice(0, 150));

        emitMetricEvent<MulticallTimeoutEvent>({
          event: "multicall.timeout",
          isError: true,
          data: {
            metricType: "rpcTimeout",
            isInMainThread: !isWebWorker,
            requestType: "initial",
            rpcProvider: rpcProviderName,
            errorMessage: _viemError.message.slice(0, 150),
          },
        });

        sendCounterEvent("timeout", {
          requestType: "initial",
          rpcProvider: rpcProviderName,
        });

        return fallbackMulticall(e).then((response) => {
          let cursor = 0;
          const ordered = originalKeys.map((_, idx) =>
            skippedMask[idx]
              ? { status: "failure", error: new Error("zeroAddress target"), result: undefined }
              : response[cursor++]
          );
          return processResponse(ordered);
        });
      });

    if (result.success) {
      return result;
    }

    emitMetricEvent<MulticallErrorEvent>({
      event: "multicall.error",
      isError: true,
      data: {
        requestType: "initial",
        rpcProvider: rpcProviderName,
        isInMainThread: !isWebWorker,
        errorMessage: serializeMulticallErrors(result.errors),
      },
    });

    sendCounterEvent("error", {
      requestType: "initial",
      rpcProvider: rpcProviderName,
    });

    if (!this.fallbackRpcSwitcher?.isFallbackMode) {
      this.fallbackRpcSwitcher?.trigger();
    }

    return await fallbackMulticall(new Error("multicall fallback error")).then(processResponse);
  }
}
